import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  viewChild,
} from '@angular/core'
import {Logo} from '../job-agent-support-box.component'
import {isPlatformBrowser, NgOptimizedImage} from '@angular/common'

@Component({
  selector: 'bgo-agency-logo',
  standalone: true,
  imports: [NgOptimizedImage],
  templateUrl: './agency-logo.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgencyLogoComponent {
  logo = input.required<Logo>()
  private readonly container = viewChild<ElementRef<HTMLDivElement>>('containerRef')
  private readonly image = viewChild<ElementRef<HTMLImageElement>>('imageRef')
  private readonly platformId = inject(PLATFORM_ID)

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      effect(() => {
        const image = this.image()
        const container = this.container()
        if (!image || !container) return

        this.setImageBackground(image.nativeElement, container.nativeElement)
      })
    }
  }

  /**
   * Sets the background color of the container to match the top-left pixel of the image.
   * Only applies if the image is not transparent.
   */
  private setImageBackground(img: HTMLImageElement, container: HTMLDivElement) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const handler = () => {
      try {
        const width = Math.min(img.naturalWidth, 100)
        const height = Math.min(img.naturalHeight, 100)

        canvas.width = width
        canvas.height = height

        ctx.drawImage(img, 0, 0, width, height)

        const imageData = ctx.getImageData(0, 0, 1, 1).data
        const [r, g, b, a] = imageData
        if (a !== 0) {
          container.style.backgroundColor = `rgb(${r}, ${g}, ${b})`
        }
      } finally {
        img.removeEventListener('load', handler)
      }
    }

    img.addEventListener('load', handler)

    // in case image is cached
    if (img.complete) {
      handler()
    }
  }
}
